import type { ScreenerCandle, ScreenerOIDataPoint } from '@/types'
import { isFreshOI, isPlottableOI } from './chartUtils'

export interface OIRulerInput {
  oiData: readonly ScreenerOIDataPoint[]
  candles: readonly ScreenerCandle[]
  /** Selection coordinates and source timestamps are Unix seconds. */
  startTime: number
  endTime: number
  interval: string
  candleInterval?: string
  currentPrice?: number | null
}

export interface OIRulerMeasurement {
  start: ScreenerOIDataPoint
  end: ScreenerOIDataPoint
  deltaCoins: number
  deltaPercent: number | null
  addedCoins: number | null
  removedCoins: number | null
  averagePrice: number | null
  startPrice: number | null
  endPrice: number | null
  currentPrice: number | null
  /** Signed net coin change valued at its estimated average price. */
  costBasisUsdt: number | null
  /** Signed net coin change valued at the selected endpoint's price. */
  endValueUsdt: number | null
  /** Signed net coin change valued at the latest price. */
  currentValueUsdt: number | null
  sampleCount: number
  barCount: number
  durationSeconds: number
  hasGaps: boolean
  staleSamples: number
  /** Whether every change extending the measured inventory had a usable price. */
  priceCoverageComplete: boolean
}

export function intervalSeconds(interval: string): number {
  const match = /^(\d+)(s|m|h|d)$/.exec(interval)
  if (!match) return 60
  const units = { s: 1, m: 60, h: 3600, d: 86400 }
  const seconds = Number(match[1]) * units[match[2] as keyof typeof units]
  return seconds > 0 ? seconds : 60
}

function validPrice(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0 ? value : null
}

/**
 * Estimates only the net OI change within the selection using a moving average.
 * Measured inventory starts at zero: changes extending it in either direction
 * add at the observed price, while offsets reduce it at the running average.
 * Crossing the starting OI resets the remaining change to the crossing price.
 * OI changes are additions/removals, not directional buys or sells or actual
 * trader entry prices. Missing OI prevents estimating intervening turnover.
 */
export function calculateOIRuler(input: OIRulerInput): OIRulerMeasurement | null {
  if (!Number.isFinite(input.startTime) || !Number.isFinite(input.endTime)) return null

  // Keep a single observation per timestamp without mutating chart data. Inputs
  // already use the Screener's receipt revision ordering; the last revision wins.
  const byTime = new Map<number, ScreenerOIDataPoint>()
  for (const point of input.oiData) {
    if (Number.isFinite(point.timestamp)) byTime.set(point.timestamp, point)
  }
  const allPoints = [...byTime.values()].sort((a, b) => a.timestamp - b.timestamp)
  const usable = (point: ScreenerOIDataPoint) => Number.isFinite(point.value)
    && point.value >= 0 && isPlottableOI(point)
  const points = allPoints.filter(usable)
  if (points.length === 0) return null

  const rangeStart = Math.min(input.startTime, input.endTime)
  const rangeEnd = Math.max(input.startTime, input.endTime)
  // The ruler is freely positioned. Only real observations inside its time
  // boundaries participate; nearby samples outside the area cannot set its
  // baseline or endpoint (including selections entirely between two samples).
  const selected = points.filter((point) => point.timestamp >= rangeStart && point.timestamp <= rangeEnd)
  if (selected.length === 0) return null
  const start = selected[0]
  const end = selected[selected.length - 1]
  const step = intervalSeconds(input.interval)
  const candleStep = intervalSeconds(input.candleInterval ?? input.interval)
  const candles = [...input.candles]
    .filter((candle) => Number.isFinite(candle.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp)

  const priceAt = (point: ScreenerOIDataPoint): number | null => {
    const snapshotPrice = validPrice(point.price)
    if (snapshotPrice != null) return snapshotPrice
    let low = 0
    let high = candles.length - 1
    while (low <= high) {
      const middle = (low + high) >> 1
      if (candles[middle].timestamp <= point.timestamp) low = middle + 1
      else high = middle - 1
    }
    const candle = candles[high]
    // Use only the candle containing this sample, never an older candle across
    // an outage or a candle in the future. OI can refresh faster than candles.
    if (!candle || point.timestamp >= candle.timestamp + candleStep) return null
    return validPrice(candle.close)
  }

  const hasGaps = selected.some((point, index) => index > 0
    && point.timestamp - selected[index - 1].timestamp > step * 1.5)
    || allPoints.some((point) => point.timestamp > start.timestamp
      && point.timestamp < end.timestamp && !usable(point))
  const startPrice = priceAt(start)
  const endPrice = priceAt(end)
  const currentPrice = validPrice(input.currentPrice)
  let costBasis: number | null = 0
  let priceCoverageComplete = true
  let addedCoins = 0
  let removedCoins = 0

  for (let index = 1; index < selected.length; index += 1) {
    const previous = selected[index - 1]
    const point = selected[index]
    const delta = point.value - previous.value
    if (delta === 0) continue
    if (delta > 0) addedCoins += delta
    else removedCoins -= delta

    const previousChange = previous.value - start.value
    const change = point.value - start.value
    if (change === 0) {
      costBasis = 0
    } else if (previousChange === 0 || Math.sign(delta) === Math.sign(previousChange)) {
      // Extend the selected change, including a net decline from the baseline.
      const price = priceAt(point)
      if (price == null) priceCoverageComplete = false
      costBasis = costBasis != null && price != null ? costBasis + delta * price : null
    } else if (Math.sign(change) !== Math.sign(previousChange)) {
      // The offset clears the prior change; only the excess opens a new one.
      const price = priceAt(point)
      if (price == null) priceCoverageComplete = false
      costBasis = price == null ? null : change * price
    } else {
      // Partial offsets preserve the average price of the remaining change.
      costBasis = costBasis != null ? (costBasis / previousChange) * change : null
    }
  }

  const durationSeconds = end.timestamp - start.timestamp
  const deltaCoins = end.value - start.value
  const costBasisUsdt = deltaCoins === 0 ? 0 : hasGaps ? null : costBasis
  return {
    start,
    end,
    deltaCoins,
    deltaPercent: start.value > 0 ? (deltaCoins / start.value) * 100 : null,
    addedCoins: hasGaps ? null : addedCoins,
    removedCoins: hasGaps ? null : removedCoins,
    averagePrice: costBasisUsdt != null && deltaCoins !== 0 ? costBasisUsdt / deltaCoins : null,
    startPrice,
    endPrice,
    currentPrice,
    costBasisUsdt,
    endValueUsdt: deltaCoins === 0 ? 0 : endPrice == null ? null : deltaCoins * endPrice,
    currentValueUsdt: deltaCoins === 0 ? 0 : currentPrice == null ? null : deltaCoins * currentPrice,
    sampleCount: selected.length,
    barCount: Math.round(durationSeconds / step),
    durationSeconds,
    hasGaps,
    staleSamples: selected.filter((point) => !isFreshOI(point)).length,
    priceCoverageComplete,
  }
}
